import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { isBctStaff, useAuthStore } from '@/stores/auth';
import { WelcomeWizard } from './WelcomeWizard';

/**
 * Self-contained mount point for WelcomeWizard.
 * Checks onboarding state and shows wizard for first-time customer users.
 * Mount this alongside WhatsNewModal in App.tsx.
 */
export function WelcomeWizardMount() {
  const { user, isAuthenticated, loginContext } = useAuthStore();
  const [open, setOpen] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!(isAuthenticated && user) || checkedRef.current) return;
    checkedRef.current = true;

    // Only show on fresh login, not on page refresh/token restore
    if (loginContext !== 'fresh') return;
    // Staff users never see the welcome wizard
    if (isBctStaff(user.role)) return;

    async function checkWelcomeState() {
      try {
        const response = await api.get<{ tours?: Record<string, unknown> }>('/users/me/onboarding');
        const state = response.data;
        // Welcome is tracked as a special tour entry '__welcome'
        const welcomeDone = !!(state?.tours as Record<string, { completed?: boolean }>)?.[
          '__welcome'
        ]?.completed;
        if (!welcomeDone) {
          setOpen(true);
        }
      } catch {
        // Don't show wizard if we can't check state
      }
    }

    checkWelcomeState();
  }, [isAuthenticated, user, loginContext]);

  const handleComplete = async () => {
    setOpen(false);
    try {
      await api.patch('/users/me/onboarding', {
        tourId: '__welcome',
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
    try {
      await api.post('/analytics/onboarding', {
        eventType: 'welcome_completed',
        eventData: {},
      });
    } catch {
      // Analytics should never break the app
    }
  };

  if (!open) return null;

  return <WelcomeWizard open={open} onComplete={handleComplete} />;
}
